import { icons } from '@/helpers/icons'
import { TAGS_FILTER_PARAM } from '@/constants/tags'

// Filter type constants
export const FILTER_TYPE = {
  COMBOBOX: 'combobox',      // Intermediate level - select to drill down
  CHECKBOX: 'checkbox',      // Leaf level - multi-select with checkboxes
  PRICE: 'price',            // Special price filter
  QUANTITY: 'quantity',      // Special quantity filter
  CUSTOM_FIELD: 'custom_field', // Text input filter for custom fields
  FAST_ACTION: 'fast_action', // Fast action filters (emit only)
  RESTOCKABLE: 'restockable'  // Top-level switch: on = send param, off = don't send
}

export const CATEGORIES_FILTER_ITEM = { icon: { is_local: true, value: icons.icon_multisport }, name: 'Categories', field: 'categories', key_name: 'categories', fetchFn: 'fetchCategoryOptions', filterType: FILTER_TYPE.CHECKBOX }
export const BRAND_FILTER_ITEM = { icon: 'heroicons:tag', name: 'Brand', field: 'brand', key_name: 'brand', fetchFn: 'fetchFieldFilterOptions', useFetchFn: true, filterType: FILTER_TYPE.CHECKBOX }
export const ATTRIBUTE_FILTER_ITEM = { icon: 'heroicons:information-circle', name: 'Attribute', field: 'attributes', key_name: 'attribute', fetchFn: 'fetchAttributeOptions', filterType: FILTER_TYPE.CHECKBOX }
export const GRADED_FILTER_ITEM = { icon: 'heroicons:scale-20-solid', name: 'Graded', field: 'graded', key_name: 'graded', fetchFn: 'fetchGradedOptions', filterType: FILTER_TYPE.CHECKBOX }
export const GRADE_FILTER_ITEM = { icon: 'heroicons:scale-20-solid', name: 'Grader', field: 'grader', key_name: 'grader', fetchFn: 'fetchGraderOptions', filterType: FILTER_TYPE.CHECKBOX }
export const YEAR_FILTER_ITEM = { icon: 'heroicons:calendar', name: 'Year', field: 'year', key_name: 'year', fetchFn: 'fetchFieldFilterOptions', useFetchFn: true, filterType: FILTER_TYPE.CHECKBOX }
export const BOX_FILTER_ITEM = { icon: 'heroicons:archive-box', name: 'Box', field: 'box', key_name: 'box', fetchFn: 'fetchFieldFilterOptions', useFetchFn: true, filterType: FILTER_TYPE.CHECKBOX }
export const ROW_FILTER_ITEM = { icon: 'heroicons:queue-list', name: 'Row', field: 'row', key_name: 'row', fetchFn: 'fetchFieldFilterOptions', useFetchFn: true, filterType: FILTER_TYPE.CHECKBOX }
export const SHELF_FILTER_ITEM = { icon: 'heroicons:rectangle-stack', name: 'Shelf', field: 'shelf', key_name: 'shelf', fetchFn: 'fetchFieldFilterOptions', useFetchFn: true, filterType: FILTER_TYPE.CHECKBOX }
export const CABINET_FILTER_ITEM = { icon: 'heroicons:building-library', name: 'Cabinet', field: 'cabinet', key_name: 'cabinet', fetchFn: 'fetchFieldFilterOptions', useFetchFn: true, filterType: FILTER_TYPE.CHECKBOX }

// Location filter - parent with nested children
export const LOCATION_FILTER_ITEM = { 
  icon: 'heroicons:map-pin', 
  name: 'Location', 
  key_name: 'location', 
  filterType: FILTER_TYPE.COMBOBOX,
  children: [
    BOX_FILTER_ITEM,
    ROW_FILTER_ITEM,
    SHELF_FILTER_ITEM,
    CABINET_FILTER_ITEM,
  ]
}
export const TEAM_FILTER_ITEM = { icon: 'heroicons:information-circle', name: 'Team', field: 'team', key_name: 'team', fetchFn: 'fetchFieldFilterOptions', useFetchFn: true, filterType: FILTER_TYPE.CHECKBOX }
export const SET_FILTER_ITEM = { icon: 'heroicons:information-circle', name: 'Set', field: 'set', key_name: 'set', fetchFn: 'fetchFieldFilterOptions', useFetchFn: true, filterType: FILTER_TYPE.CHECKBOX }
// CDP-2435 — multi-select Batch filter (Inventory only). Options from backend `available_filters.batch` ({ value, name, count }); selection serializes to the dedicated `batch_id=12,34` query param (see generateQueryString).
// `remote`: the option list is not built from the inventory response — it is
// read a page at a time from the batches endpoint, and searched there (see
// `useBatchFilterOptions`).
export const BATCH_FILTER_ITEM = { icon: 'heroicons:inbox-stack', name: 'Batch', field: 'batch', key_name: 'batch', fetchFn: 'fetchBatchOptions', filterType: FILTER_TYPE.CHECKBOX, remote: true }
// Batches per page in the Batch filter's list.
export const BATCH_FILTER_PAGE_SIZE = 50
// Multi-select Tags filter (Inventory only). Options come from the backend's
// `available_filters.tags` ({ value, name, color, count }) so the list is the tags
// actually PRESENT on cards, not the whole library — a filter offering a tag that
// matches nothing is a dead end. Selection serializes to the dedicated `tags=`
// param (see generateQueryString); the "No tags" row rides in the same param.
export const TAGS_FILTER_ITEM = { icon: 'cdp:tags', name: 'Tags', field: 'tags', key_name: 'tags', fetchFn: 'fetchTagOptions', filterType: FILTER_TYPE.CHECKBOX }
export const STATUS_TYPES_FILTER_ITEM = { icon: { is_local: true, value: icons.status_spinner }, name: 'Status', field: 'status', key_name: 'status', fetchFn: 'fetchStatusTypesOptions', filterType: FILTER_TYPE.CHECKBOX }

// Synthetic "All" row at the top of a checkbox filter: selecting it clears every
// other choice in that filter rather than sending a value. Replaces the status
// TABS in the new design, where "All" was how you cleared the status filter.
export const ALL_FILTER_OPTION_KEY = '__all__'

// New-design relabelling of the statuses the backend reports in
// `available_filters.status`. Key = backend value, value = what we show.
export const STATUS_FILTER_LABELS = {
  ready_to_list: 'Ready',
}

// Statuses the NEW design does not offer in the Status filter menu.
//
// `available` is the backend's umbrella bucket: it counts everything that is
// LISTED *plus* everything that is READY to list. Offered beside "Ready" it
// gives two rows whose results overlap and whose counts don't add up, and the
// 5-state model the new design shows in the Status column (`CARD_STATUS_META`)
// has no such state at all. Hidden rather than relabelled — there is no honest
// label for "two of the other rows, added together".
//
// Classic design is untouched: "Available" is its own vocabulary there, and its
// status tabs (`INVENTORY_FILTERS_OPTIONS`) still offer it.
export const HIDDEN_STATUS_FILTER_OPTIONS = ['available']

// Status TAB label → the `Status` filter entry it applies (the same shape the
// filter menu produces, so a tab and a menu selection are interchangeable).
// Serialized by `generateQueryString` as `status=<key_name>`. 'All' clears it.
// Every tab any surface offers lives here; each surface picks its own subset
// via `status_tabs.options`.
export const STATUS_TAB_FILTERS = {
  'For Sale':      { name: 'For Sale',      value: 'for_sale',      key_name: 'for_sale',      icon: icons.shopping_cart },
  'Ended':         { name: 'Ended',         value: 'ended',         key_name: 'ended',         icon: icons.stop },
  'Not For Sale':  { name: 'Not For Sale',  value: 'ended',         key_name: 'ended',         icon: icons.ready_to_list },
  'Available':     { name: 'Available',     value: 'available',     key_name: 'available',     icon: icons.ready_to_list },
  'Draft':         { name: 'Draft',         value: 'draft',         key_name: 'draft',         icon: icons.pencil_square },
  'Ready':         { name: 'Ready',         value: 'ready_to_list', key_name: 'ready_to_list', icon: icons.ready_to_list },
  // The backend has no `listed` status — an item that's live on a marketplace is
  // `available` there, so the "Listed" tab sends that. Label ≠ key on purpose.
  'Listed':        { name: 'Listed',        value: 'available',     key_name: 'available',     icon: icons.ready_to_list },
  'Sold Out':      { name: 'Sold Out',      value: 'sold_out',      key_name: 'sold_out',      icon: icons.ready_to_list },
  'Out of Stock':  { name: 'Out of Stock',  value: 'out_of_stock',  key_name: 'out_of_stock',  icon: icons.ready_to_list },
}
export const LISTINGS_FILTER_ITEM = { icon: 'heroicons:shopping-cart', field: 'listing', name: 'Listing', key_name: 'listing', fetchFn: 'fetchListingOptions', filterType: FILTER_TYPE.CHECKBOX }
export const PRICE_FILTER_ITEM = { icon: 'heroicons:currency-dollar', name: 'Price', key_name: 'price', filterType: FILTER_TYPE.PRICE }
export const QUANTITY_FILTER_ITEM = { icon: 'heroicons:hashtag', name: 'Quantity', key_name: 'quantity', filterType: FILTER_TYPE.QUANTITY }
export const MISSING_DETAILS_FILTER_ITEM = { icon: 'heroicons:exclamation-triangle', name: 'Missing Details', key_name: 'missing_details', filterType: FILTER_TYPE.FAST_ACTION, fastActionIndex: 0 }
export const SUGGESTED_EDITS_FILTER_ITEM = { icon: 'heroicons:light-bulb', name: 'Suggested Edits', key_name: 'suggested_edits', filterType: FILTER_TYPE.FAST_ACTION, fastActionIndex: 1 }
export const UNREVIEWED_FILTER_ITEM = { icon: 'heroicons:eye-slash', name: 'Unreviewed', key_name: 'unreviewed', filterType: FILTER_TYPE.FAST_ACTION, isUnreviewed: true }
export const RESTOCKABLE_FILTER_ITEM = { icon: 'heroicons:arrow-path', name: 'Restockable', field: 'restockable', key_name: 'restockable', filterType: FILTER_TYPE.RESTOCKABLE }

// Custom Fields filter - children are dynamically generated from userStore.customFields
export const CUSTOM_FIELDS_FILTER_ITEM = { 
  icon: 'heroicons:adjustments-horizontal', 
  name: 'Custom Fields', 
  key_name: 'custom_fields', 
  filterType: FILTER_TYPE.COMBOBOX,
  isDynamic: true // Flag to indicate children should be fetched dynamically
}


export const FILTERS_BLOCK_1 = [
  STATUS_TYPES_FILTER_ITEM,
  LISTINGS_FILTER_ITEM,
  PRICE_FILTER_ITEM,
  // CATEGORIES_FILTER_ITEM,
  // ATTRIBUTE_FILTER_ITEM,
  // YEAR_FILTER_ITEM,
  // SET_FILTER_ITEM,
  // BRAND_FILTER_ITEM,
  // TEAM_FILTER_ITEM,
  // GRADED_FILTER_ITEM,
  // GRADE_FILTER_ITEM,
  // LOCATION_FILTER_ITEM,
  // QUANTITY_FILTER_ITEM,
  // CUSTOM_FIELDS_FILTER_ITEM,
  // RESTOCKABLE_FILTER_ITEM,
  // { icon: { is_local: true, value: icons.text_T }, name: 'Content' }
]

export const BATCH_DETAIL_FILTERS_BLOCK_1 = [
  STATUS_TYPES_FILTER_ITEM,
  CATEGORIES_FILTER_ITEM,
  ATTRIBUTE_FILTER_ITEM,
  BRAND_FILTER_ITEM,
  GRADED_FILTER_ITEM,
  GRADE_FILTER_ITEM,
]

export const LISTINGS_FILTERS_BLOCK_1 = [
  STATUS_TYPES_FILTER_ITEM,
]

export const FILTER_CONFIGS = [
  CATEGORIES_FILTER_ITEM,
  BATCH_FILTER_ITEM,
  TAGS_FILTER_ITEM,
  BRAND_FILTER_ITEM,
  ATTRIBUTE_FILTER_ITEM,
  TEAM_FILTER_ITEM,
  SET_FILTER_ITEM,
  GRADED_FILTER_ITEM,
  GRADE_FILTER_ITEM,
  YEAR_FILTER_ITEM,
  BOX_FILTER_ITEM,
  ROW_FILTER_ITEM,
  SHELF_FILTER_ITEM,
  CABINET_FILTER_ITEM,
  STATUS_TYPES_FILTER_ITEM,
  LISTINGS_FILTER_ITEM,
  PRICE_FILTER_ITEM,
  QUANTITY_FILTER_ITEM,
]

export const AVAILABLE_FILTERS = {
  attribute: [],
  brand: [],
  category: [],
  team: [],
  set: [],
  graded: [],
  grader: [],
  team: [],
  year: [],
  box: [],
  row: [],
  shelf: [],
  cabinet: [],
}

// Listings filter condition options
export const LISTINGS_FILTER_CONDITION_OPTIONS = [
  { value: 'is', label: 'is', paramKey: 'listedOn' },
  { value: 'is_not', label: 'is not', paramKey: 'notListedOn' }
]

// Listings platform keys mapping (from key_name to URL param value)
export const LISTINGS_FILTER_CONNECTION_MAP = {
  ebayListed: 'ebay',
  collxListed: 'collx',
  whatnotListed: 'whatnot',
  shopifyListed: 'shopify',
  hasNoListings: 'none'
}

// Reverse map: platform name → key_name (e.g. 'ebay' → 'ebayListed')
export const LISTINGS_PLATFORM_TO_KEY_MAP = Object.fromEntries(
  Object.entries(LISTINGS_FILTER_CONNECTION_MAP).map(([key, platform]) => [platform, key])
)

// Params that should be extracted from itemFilters and passed as base/query params (not in filter string)
// FilterMenu viewport-collision positioning (px). Used to decide whether the
// parent panel right-aligns and whether the nested drill-down flips left.
export const FILTER_NESTED_PANEL_WIDTH = 384 // nested drill-down panel (max-w-96 = 24rem)
export const FILTER_PARENT_PANEL_WIDTH = 384 // parent panel (md:w-96)
export const FILTER_VIEWPORT_MARGIN = 8      // min gap kept from the viewport edge
// Below this width the parent + nested panels can't sit side by side, so drilling
// in hides the parent and shows only the nested panel (Tailwind `md`).
export const FILTER_MOBILE_BREAKPOINT = 768

// Keys that ride in `itemFilters` / the URL query without being a FILTER: how
// the list is ordered, paged, searched or viewed. Used to tell "nothing is
// filtered" apart from "the query object is empty".
export const NON_FILTER_QUERY_KEYS = ['sortBy', 'q', 'page', 'perPage', 'duplicate', 'tab']

export const BASE_PARAM_KEYS = [
  'sortBy',
  'status',
  'priceMin',
  'priceMax',
  'priceBetween',
  'quantityMin',
  'quantityMax',
  'quantityBetween',
  'q',
  'listedOn',
  'notListedOn',
  'customField',
  'restockable',
  'batch_id', // CDP-2435 — dedicated top-level param (must NOT fold into the generic `filter=`)
  TAGS_FILTER_PARAM, // Product Tags — dedicated top-level param, same reason as batch_id
  // Fast actions (Missing Details · Suggested Edits · Unreviewed). The BE takes
  // these as their own params — `with_errors=1`, `with_suggested_edits=1`,
  // `viewed=0` — not inside `filter=`. `generateQueryString` already maps the
  // key_name to 1/0; listing them here is what keeps them top-level.
  'with_errors',
  'with_suggested_edits',
  'viewed',
]

// Metadata for building filter structures from backend/saved-view values (used by parseBackendFilters)
// `icon` = used as the item-level icon (item.icon) — for filters where type icon doubles as item icon
// `selectedFilterIcon` = used as the parent badge icon (item.selectedFilterIcon) — for all others
export const FILTER_METADATA = {
  // Filters that use icon as item-level icon
  category: { icon: CATEGORIES_FILTER_ITEM.icon, selectedFilter: 'Categories' },
  brand: { icon: BRAND_FILTER_ITEM.icon, selectedFilter: 'Brand' },
  attribute: { icon: ATTRIBUTE_FILTER_ITEM.icon, selectedFilter: 'Attribute' },
  year: { icon: YEAR_FILTER_ITEM.icon, selectedFilter: 'Year' },
  status: { icon: STATUS_TYPES_FILTER_ITEM.icon, selectedFilter: 'Status' },

  // Filters that use selectedFilterIcon as badge-level icon
  graded: { selectedFilterIcon: GRADED_FILTER_ITEM.icon, selectedFilter: 'Graded' },
  grader: { selectedFilterIcon: GRADE_FILTER_ITEM.icon, selectedFilter: 'Grader' },
  team: { selectedFilterIcon: TEAM_FILTER_ITEM.icon, selectedFilter: 'Team' },
  set: { selectedFilterIcon: SET_FILTER_ITEM.icon, selectedFilter: 'Set' },

  // Location children
  box: { selectedFilterIcon: BOX_FILTER_ITEM.icon, selectedFilter: 'Box' },
  row: { selectedFilterIcon: ROW_FILTER_ITEM.icon, selectedFilter: 'Row' },
  shelf: { selectedFilterIcon: SHELF_FILTER_ITEM.icon, selectedFilter: 'Shelf' },
  cabinet: { selectedFilterIcon: CABINET_FILTER_ITEM.icon, selectedFilter: 'Cabinet' },

  // Listings (per-platform flags from backend)
  ebayListed: { selectedFilterIcon: LISTINGS_FILTER_ITEM.icon, selectedFilter: 'Listing', isListing: true, displayName: 'Ebay' },
  collxListed: { selectedFilterIcon: LISTINGS_FILTER_ITEM.icon, selectedFilter: 'Listing', isListing: true, displayName: 'CollX' },
  whatnotListed: { selectedFilterIcon: LISTINGS_FILTER_ITEM.icon, selectedFilter: 'Listing', isListing: true, displayName: 'Whatnot' },
  shopifyListed: { selectedFilterIcon: LISTINGS_FILTER_ITEM.icon, selectedFilter: 'Listing', isListing: true, displayName: 'Shopify' },
  hasNoListings: { selectedFilterIcon: LISTINGS_FILTER_ITEM.icon, selectedFilter: 'Listing', isListing: true, displayName: 'No Listings' },

  // Price range filters
  priceMin: { selectedFilterIcon: PRICE_FILTER_ITEM.icon, selectedFilter: 'Price Min', isPrice: true },
  priceMax: { selectedFilterIcon: PRICE_FILTER_ITEM.icon, selectedFilter: 'Price Max', isPrice: true },
  priceBetween: { selectedFilterIcon: PRICE_FILTER_ITEM.icon, selectedFilter: 'Price Between', isPrice: true, isBetween: true },

  // Quantity range filters
  quantityMin: { selectedFilterIcon: QUANTITY_FILTER_ITEM.icon, selectedFilter: 'Quantity Min', isQuantity: true },
  quantityMax: { selectedFilterIcon: QUANTITY_FILTER_ITEM.icon, selectedFilter: 'Quantity Max', isQuantity: true },
  quantityBetween: { selectedFilterIcon: QUANTITY_FILTER_ITEM.icon, selectedFilter: 'Quantity Between', isQuantity: true, isBetween: true },

  // Restockable (boolean switch)
  restockable: { selectedFilterIcon: RESTOCKABLE_FILTER_ITEM.icon, selectedFilter: 'Restockable', isRestockable: true },

  // Custom fields (fieldName:value format)
  customField: { selectedFilterIcon: CUSTOM_FIELDS_FILTER_ITEM.icon, selectedFilter: 'customField', isCustomField: true },
}

export const PRICE_FILTER_CONFIG = {
  fieldMin: 'priceMin',
  fieldMax: 'priceMax',
  fieldBetween: 'priceBetween',
  minLabel: 'Price Min',
  maxLabel: 'Price Max',
  betweenLabel: 'Price Between',
  minPlaceholder: 'min',
  maxPlaceholder: 'max',
  betweenPlaceholder: '0-0',
  step: 0.01,
  rangeLabel: 'Price Range',
  type: 'price',
  icon: 'heroicons:currency-dollar',
}

export const QUANTITY_FILTER_CONFIG = {
  fieldMin: 'quantityMin',
  fieldMax: 'quantityMax',
  fieldBetween: 'quantityBetween',
  minLabel: 'Quantity Min',
  maxLabel: 'Quantity Max',
  betweenLabel: 'Quantity Between',
  minPlaceholder: 'min',
  maxPlaceholder: 'max',
  betweenPlaceholder: '0-0',
  step: 1,
  rangeLabel: 'Quantity Range',
  type: 'quantity',
  icon: 'heroicons:hashtag',
}
